import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { SessionUser } from '@bidmanager/shared';
import * as api from './api';

interface AuthState {
  user: SessionUser | null;
  ready: boolean;
  signIn: (email: string, password: string, clientSlug?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<SessionUser | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // The access token is memory-only, so every page load starts by exchanging
    // the stored refresh token. `ready` gates rendering so the app never
    // flashes the login screen at an already-signed-in user.
    void api.restore().then((restored) => {
      setUser(restored);
      setReady(true);
    });
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      ready,
      signIn: async (email, password, clientSlug) => {
        // Cleared on every auth transition: React Query outlives the session,
        // and cached rows from the previous account would otherwise keep
        // rendering for the next one — a cross-tenant leak on a shared machine.
        queryClient.clear();
        setUser(await api.login(email, password, clientSlug));
      },
      signOut: async () => {
        await api.logout();
        queryClient.clear();
        setUser(null);
      },
    }),
    [user, ready, queryClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
